import React, { useEffect } from 'react';
import { observer } from 'mobx-react';
import { useViewerControl } from 'libs/rendering/gltf/hooks';
import { useClientStore } from 'apps/hooks';

import { useViewerWidth } from 'libs/rendering/hooks';

import Icon from 'components/Icon';
import Heading from 'components/Heading';
import Twix from 'components/Twix';
import Button from 'components/Button';
import Tooltip from 'components/Tooltip';

import { FrameHeaderHeight, SubHeaderHeight } from 'styles/constants';

import { isInDesktopMode } from 'libs/gutClient/electronBridge';

import { useDesktopCompatibility } from 'hooks/useDesktopCompatible';

import { UiModes } from '../../config';

import ResizePanel from './resizePanel';

import {
  MessagePane,
  PanelStyled,
  PanelHeaderMainWrapper,
  TitleWrapper,
  BlockWrapper,
  IconWrapper,
  TooltipWrapper,
  CloseButtonWrapper,
} from './panel.styled';

const Panel = observer(function _Panel(props) {
  const viewerControl = useViewerControl();
  const clientStore = useClientStore();
  const compatibleWithTab = useDesktopCompatibility({
    feature: 'tab',
  });

  const desktopMode = isInDesktopMode();

  const {
    position,
    leftPanelState,
    title,
    iconName,
    viewBox,
    children,
    footer,
    isOpen,
    overlay,
    overlayStyle,
    className,
    style,
    dataTestId,
  } = props;

  const [canvasWidth, rightOffset] = useViewerWidth();
  useEffect(() => {
    viewerControl.viewer?.updateCanvas();
  }, [canvasWidth, rightOffset]);

  // alias collapseAction
  const collapseAction = () => {
    if (leftPanelState) leftPanelState.Collapse();
    else if (props.collapseAction) props.collapseAction();
    else viewerControl?.UpdateUiMode(UiModes.collapsed);
  };

  const CloseButton = () =>
    props.addCloseButton ? (
      <CloseButtonWrapper>
        <Button
          outline
          size='small'
          variant='minimal'
          icon='close_small'
          ariaLabel='close panel'
          onClick={collapseAction}
        />
      </CloseButtonWrapper>
    ) : null;

  const PanelHeader = () =>
    !props.removeHeader ? (
      <>
        {title && (
          <PanelHeaderMainWrapper position={position}>
            {iconName && (
              <IconWrapper>
                <Icon name={iconName} viewBox={viewBox} size='xmedium' />
              </IconWrapper>
            )}
            <TitleWrapper>
              <Heading level='3' size='small'>
                {title}
              </Heading>
            </TitleWrapper>

            <BlockWrapper onClick={collapseAction}>
              <TooltipWrapper>
                <Tooltip delayShow={500} place='right' id='tooltipTab' />
              </TooltipWrapper>
              {/* <ButtonCollapse */}
              {/*  icon={position === 'left' ? 'collapse' : 'collapse_right'} */}
              {/*  ariaLabel='Collapse Tab' */}
              {/*  size='xsmall' */}
              {/*  variant='secondary' */}
              {/*  data-tip='Collapse' */}
              {/*  data-place='right' */}
              {/*  data-for='tooltipTab' */}
              {/* /> */}
            </BlockWrapper>
          </PanelHeaderMainWrapper>
        )}
      </>
    ) : null;

  /*
    FIXME: below causes {children} to be remounted
    const DefaultPanel = () => (
      <PanelStyled
        position={position}
        isOpen={isOpen}
        large={props.large}
        chatVisible={props.chatVisible}
      >
        <PanelHeader />
        <CloseButton />
        {children}
        {footer}
      </PanelStyled>
    );
  */

  return (
    <Twix breakingPoint='medium'>
      <PanelStyled
        data-testid={dataTestId}
        position={position}
        style={style}
        isOpen={isOpen}
        overlay={overlay}
        overlayStyle={overlayStyle}
        large={props.large}
        top={props.top}
        className={className}
        chatVisible={props.chatVisible}
        width={`${rightOffset}px`}
        overrideLeftToZero={!clientStore.signedInProfile}
      >
        <PanelHeader />
        {children}
        {footer && <MessagePane id='message-pane'>{footer}</MessagePane>}
      </PanelStyled>
      <>
        {leftPanelState ? (
          <ResizePanel
            position={position}
            isOpen={isOpen}
            storedLeftPanelWidth={leftPanelState.leftPanelResizeWidth}
            updateOutsideWidthSize={leftPanelState.SetLeftPanelResizeWidth}
          >
            <PanelHeader />
            {children}
            {footer}
          </ResizePanel>
        ) : (
          <PanelStyled
            position={position}
            isOpen={isOpen}
            top={
              desktopMode && compatibleWithTab
                ? FrameHeaderHeight
                : FrameHeaderHeight + SubHeaderHeight
            }
            large={props.large}
            chatVisible={props.chatVisible}
          >
            <PanelHeader />
            <CloseButton />
            {children}
            {footer}
          </PanelStyled>
        )}
      </>
    </Twix>
  );
});

export default Panel;
